import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { FolderTabs, type FolderTab } from '../../../../components/FolderTabs';
import { Modal } from '../../../../components/Modal';
import type { MenuItem } from '../../../../components/Menu';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { formatDate, formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { ITEM_KIND_LABEL, OWNERSHIP_LABEL } from '../inventoryTypes';
import { stockBatchesApi, stockMovementsApi, stockUnitsApi } from '../stockApi';
import type {
  StockBatchListItem,
  StockLevelListItem,
  StockMovementListItem,
  StockUnitListItem,
} from '../stockTypes';
import {
  BATCH_STATUS_LABEL,
  STOCK_STATUS_LABEL,
  UNIT_STATUS,
  UNIT_STATUS_LABEL,
  unitConditionTone,
  movementTypeLabel,
  stockStatusTone,
} from '../stockTypes';

/**
 * Which panel is showing. The second one is named for what the item actually holds.
 */
export type StockDetailTab = 'info' | 'stock' | 'movements';

/**
 * ONE quantity, with its unit.
 *
 * The figure is `mono tabular-nums` like every machine value, and a ZERO is muted — the
 * product's own rule, which the reference screen this was captured from does not follow. A
 * stock panel is read by finding the figures that are NOT zero, and dimming the rest is what
 * lets the eye do that without reading the column.
 *
 * The unit is a step further back again: it repeats on every row, so at full strength a column
 * of "ream" competes with the numbers it is qualifying.
 */
function quantity(value: number, unitCode: string): ReactNode {
  return (
    <span className="font-mono tabular-nums">
      <span className={value === 0 ? 'text-slate-300' : undefined}>{value}</span>{' '}
      <span className="text-slate-400">{unitCode}</span>
    </span>
  );
}

/**
 * A figure this product cannot answer yet — an em dash, never a zero.
 *
 * The distinction is the whole reason the row is drawn at all: a zero would claim nothing is on
 * loan, which is a fact about the STOCK, where an em dash says the product cannot answer, which
 * is a fact about the PRODUCT. It is the same call the table's own ISSUED band takes, and the
 * two must agree — a reader who sees an em dash in the column and a zero in this panel would be
 * looking at one item described two ways.
 */
const PENDING: ReactNode = <span className="font-mono text-slate-400">—</span>;

/**
 * A figure that AGES and has not arrived yet — a skeleton, never a zero and never an em dash.
 *
 * Three different absences, and the panel has to tell them apart:
 *
 * - `PENDING` (an em dash) — the PRODUCT cannot answer. Sold, which nothing writes.
 * - `null` — the RECORD has no value. No investment recorded, no lease end.
 * - this — the value EXISTS and the call carrying it is still in flight.
 *
 * The ten aging figures are left at `default(int)` by `Project` and filled by `WithAging`, so
 * printing the row's own value during that window renders a muted ZERO — which is exactly what
 * "none expired" looks like, and a wrong answer a reader cannot tell from a right one. The table
 * draws a skeleton in those cells for the same reason and corrects itself when the call lands;
 * this panel has no later fill, so it would hold the zero for as long as it is open.
 *
 * Sized to one quantity so the row does not change height when the real figure replaces it.
 */
const AGING_PENDING: ReactNode = <Skeleton className="h-4 w-12" />;

/**
 * Everything about one item's stock, without leaving the list.
 *
 * **Captured from the reference's own stock detail on 20 Sep 2026**, and it replaces two
 * navigations rather than adding a third surface: the row anchor used to open the item's
 * catalog profile — which answers what the item IS and not what is held — and the kebab's Stock
 * detail landed on the whole Stock Units or Stock Batches list, which CLAUDE.md already recorded
 * as a shortfall because neither index reads a filter from the URL. This answers the question
 * both were reaching for.
 *
 * **The Item Info tab FETCHES NOTHING.** Every value on it is already on the level row the
 * reader clicked, which is what makes opening it free — and it is also what keeps the panel and
 * the table honest with each other, because they are reading one object rather than two answers
 * to one question.
 *
 * **The other two tabs fetch on FIRST OPEN and are then cached for the visit**, which is the
 * profile page's own lazy rule at modal scale: a reader who wanted the summary should not pay
 * for two list queries, and a reader who opens Batches twice should not pay twice.
 *
 * **The second tab is named for what the item HOLDS** — Units for a serialized item, Batches for
 * one counted in quantities. `tracksSerial` decides, which is the same flag the row kebab
 * branches on, and it is on the row for exactly this kind of decision.
 *
 * **It writes nothing, so it carries no confirm tick and no primary action.** Close is the only
 * control — the `<ImagePreviewModal>` precedent: a tick in front of "look at this" is ceremony
 * that teaches readers to click past ticks.
 */
export function StockDetailModal({
  open,
  level,
  agingKnown,
  initialTab = 'info',
  onClose,
  onIssue,
  onReturn,
  onSetCondition,
  onOpenUnit,
}: {
  open: boolean;
  /** The row that was clicked. Null closes the panel. */
  level: StockLevelListItem | null;
  /**
   * Whether that row's TEN AGING figures are real — the table says so when it hands the row
   * over. False means the aging call had not landed, so those rows draw `AGING_PENDING` rather
   * than the zeros `Project` left in them. This panel fetches nothing for its first tab, so it
   * has no later fill of its own: without the flag it would hold a muted zero and a red "Out of
   * stock" pill on an item that is Low with three available, for as long as it is open.
   */
  agingKnown: boolean;
  /** Which tab to land on — the kebab's Stock detail opens straight onto the stock one. */
  initialTab?: StockDetailTab;
  onClose: () => void;

  /*
    THE UNIT ROW'S OWN VERBS, handed UP rather than performed here.

    Every one of them opens a form, and this is already a modal — "one modal, never stacked" is
    enforced everywhere else in this product, so the caller CLOSES this panel and opens the form
    beside it. That is the registration hand-off's shape, one surface along.

    **An absent callback removes its entry**, which is the idiom `<EditableAvatar>` already uses:
    a surface that cannot open the form never offers the verb, rather than drawing one that goes
    nowhere.
  */
  onIssue?: (unit: StockUnitListItem) => void;
  onReturn?: (unit: StockUnitListItem) => void;
  onSetCondition?: (unit: StockUnitListItem) => void;
  onOpenUnit?: (unit: StockUnitListItem) => void;
}) {
  /*
    The two collection tabs carry the keys of the MODULES they read, never this page's own.
    `<FolderTabs>` hides a gated tab and lands the reader on the first one they can see, so a
    reader without either simply gets Item Info — which is the row they already have.
  */
  const canReadBatches = usePermission('stock-batches.read');
  const canReadUnits = usePermission('stock-units.read');
  const canReadMovements = usePermission('stock-movements.read');

  const [tab, setTab] = useState<StockDetailTab>(initialTab);

  const [batches, setBatches] = useState<StockBatchListItem[] | null>(null);
  const [movements, setMovements] = useState<StockMovementListItem[] | null>(null);
  const [loading, setLoading] = useState(false);

  const itemId = level?.id ?? null;
  const serialized = level?.tracksSerial ?? false;

  /*
    A DIFFERENT item drops everything first, in its own effect. Without it the reader opening a
    second row would see the previous item's lots under the new item's title until the fetch
    landed — the profile page's own first-load rule, and the failure it names.
  */
  useEffect(() => {
    setBatches(null);
    setMovements(null);
    setTab(initialTab);
  }, [itemId, initialTab]);

  /*
    THE UNITS TAB IS A PANEL, and the other two are still previews. The test is the one the PPA
    line items already set: **does this collection get WRITTEN from where it is drawn?** A unit's
    condition is corrected from its own row here, so it is a panel — `<PreviewGrid>` is read-only
    by contract and has no actions gutter, and bolting one on would make it a third table shell.
    Nothing writes a lot or a ledger line from this modal, so those two stay as they were.

    It also drops the 200-row cap the previews carry, which this file recorded as a real limit
    rather than a silent truncation — a panel pages server-side and searches serials on the way.
  */
  const fetchUnits = useCallback(
    (query: { search?: string; page: number; pageSize: number; sort?: string }, signal: AbortSignal) =>
      stockUnitsApi.list(
        {
          search: query.search ?? '',
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          item: itemId ?? '',
          status: 'active',
        },
        signal,
      ),
    [itemId],
  );

  const unitsTable = useDataTable<StockUnitListItem>({
    fetchPage: fetchUnits,
    /* Nothing is requested until the reader opens the tab — mounting is how they ask, which is
       the same lazy rule the two previews below follow. */
    enabled: open && itemId !== null && serialized && tab === 'stock' && canReadUnits,
    defaultSort: 'serialNumber',
  });

  /*
    A COUNT NOBODY HAS ASKED FOR IS NOT DRAWN. `total` is 0 before the first answer lands, so a
    badge reading it directly would show a confident zero on an item holding forty — which is the
    call the batch badge already makes by testing its collection against null.
  */
  const [unitsCounted, setUnitsCounted] = useState(false);

  useEffect(() => {
    setUnitsCounted(false);
  }, [itemId]);

  useEffect(() => {
    if (tab === 'stock' && serialized && !unitsTable.loading) setUnitsCounted(true);
  }, [tab, serialized, unitsTable.loading]);

  /*
    FETCHED when the tab is opened, and cached for the visit. Mounting is how the reader asks:
    a summary tab that pulled two list queries nobody looked at is the cost the profile's lazy
    rule exists to avoid, one surface down.

    A page size of 200 is the whole collection for any item anybody holds, and the panels are
    read-only and unpaged — see `<PreviewGrid>`. An item past that is a real limit rather than a
    silent truncation, and the full lists are one click away on their own pages.
  */
  useEffect(() => {
    if (!open || itemId === null) return;

    /* The units tab is a PANEL and fetches through its own hook — this effect is the two
       previews' own, and a serialized item has nothing for it to do. */
    const wantsStock = tab === 'stock' && !serialized && batches === null;
    const wantsMovements = tab === 'movements' && movements === null;

    if (!wantsStock && !wantsMovements) return;

    const controller = new AbortController();
    setLoading(true);

    const request = wantsStock
      ? stockBatchesApi
          .list({ item: itemId, status: 'active', pageSize: 200 }, controller.signal)
          .then((page) => setBatches(page.items))
      : stockMovementsApi
          .list({ item: itemId, pageSize: 200 }, controller.signal)
          .then((page) => setMovements(page.items));

    request
      // A failed feed leaves the collection NULL, which the panel renders as its empty
      // sentence. It names no field, so it must not reach an error state built for a write.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, itemId, tab, serialized, batches, movements]);

  /*
    THE ROW'S VERBS — captured from the hospital's own stock-unit menu.

    Each entry carries the key of what it DOES, never this panel's own read key, and each is
    drawn only where it APPLIES. That is an absence rather than a hidden control: a unit sitting
    in the store has nothing to return, and one already out cannot be issued again — the server
    refuses both, so an entry there could never work.

    **Attach receipt is deliberately absent**, and it is the one gap rather than a decision:
    a unit has no attachment table, no endpoint and no key, and every file in this product is
    filed through `IRepositoryFiler`. It arrives as its own piece of work.
  */
  const unitActions = (row: StockUnitListItem): MenuItem[] => [
    ...(onIssue !== undefined && row.status === UNIT_STATUS.inStock
      ? [
          {
            key: 'issue',
            label: 'Issue',
            permission: 'issuances.create',
            onClick: () => onIssue(row),
          },
        ]
      : []),

    /*
      RETURN reads the OPEN ISSUANCE, not the status. A return is recorded against the SLIP, so
      the row has to be able to name it — `Assigned` says a unit is out and never which issuance
      put it there, which is why the projection carries the id at all.

      <b>`!= null`, never `!== null`.</b> A field that arrives UNDEFINED — an older payload, a
      caller that projects less, a row shape that grew the field after this code was written —
      is still absent, and the strict form reads it as PRESENT: the entry drew on every
      in-stock unit in the panel, and the label interpolated `undefined` where the slip number
      belongs. Same lesson as the investment filter on Stock Levels, one surface along.
    */
    ...(onReturn !== undefined && row.openIssuanceId != null
      ? [
          {
            key: 'return',
            label:
              row.openIssuanceNumber == null
                ? 'Return to stock'
                : `Return to stock (${row.openIssuanceNumber})`,
            permission: 'issuances.return',
            onClick: () => onReturn(row),
          },
        ]
      : []),

    ...(onSetCondition !== undefined && row.isOnHand
      ? [
          {
            key: 'condition',
            label: 'Set condition',
            permission: 'stock-units.update',
            onClick: () => onSetCondition(row),
          },
        ]
      : []),

    ...(onOpenUnit !== undefined
      ? [
          {
            key: 'open',
            label: 'Open unit',
            permission: 'stock-units.read',
            onClick: () => onOpenUnit(row),
          },
        ]
      : []),
  ];

  if (!level) return null;

  const unit = level.unitCode;

  const tabs: FolderTab<StockDetailTab>[] = [
    { key: 'info', label: 'Item Info' },
    {
      key: 'stock',
      // Named for what the item HOLDS, and counted once it has been looked at. A badge that
      // read 0 before the fetch would be a figure nobody had asked the server for.
      label: (
        <>
          {serialized ? 'Units' : 'Batches'}
          {serialized ? (
            unitsCounted ? <CountBadge count={unitsTable.total} /> : null
          ) : batches !== null ? (
            <CountBadge count={batches.length} />
          ) : null}
        </>
      ),
      permission: serialized ? 'stock-units.read' : 'stock-batches.read',
    },
    {
      key: 'movements',
      label: (
        <>
          Movement History
          {movements !== null ? <CountBadge count={movements.length} /> : null}
        </>
      ),
      permission: 'stock-movements.read',
    },
  ];

  const canSeeStock = serialized ? canReadUnits : canReadBatches;

  return (
    <Modal
      open={open}
      onClose={onClose}
      /*
        The ITEM names the panel, because that is what the reader clicked. "Stock" leads so the
        title says what is being shown rather than repeating the row they came from.
      */
      title={`Stock — ${level.name}`}
      size="3xl"
      id="stock-detail-modal"
      /*
        NO confirm tick and no primary action. This writes nothing, and the rule gates a
        TRANSACTION — a tick in front of reading is ceremony that teaches readers to click past
        ticks.
      */
      footer={
        <Button id="stock-detail-close" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <FolderTabs
          id="stock-detail-tabs"
          tabs={tabs}
          activeKey={tab}
          onChange={setTab}
          ariaLabel="Stock detail"
          paddingX="px-0"
        />

        {tab === 'info' ? <ItemInfo level={level} unit={unit} agingKnown={agingKnown} /> : null}

        {tab === 'stock' && serialized ? (
          <DataTablePanel
            id="stock-detail-units"
            state={unitsTable}
            columns={unitColumns(level)}
            rowKey={(row) => row.id}
            searchPlaceholder="Search serials"
            /* Nothing acts on a selection from a preview panel, so the gutter would be a dead
               control — the call `<UnitEmployeesModal>` already makes. */
            selectable={false}
            rowActions={unitActions}
            emptyMessage="No units are held for this item."
            emptySearchMessage="No serial matches that search."
            minWidth={760}
          />
        ) : null}

        {tab === 'stock' && !serialized ? (
          loading && batches === null ? (
            <Rows />
          ) : (
            <PreviewGrid
              columns={batchColumns(unit)}
              rows={canSeeStock ? (batches ?? []) : []}
              rowKey={(row) => row.id}
              empty="No lots are held for this item."
            />
          )
        ) : null}

        {tab === 'movements' ? (
          loading && movements === null ? (
            <Rows />
          ) : (
            <PreviewGrid
              columns={movementColumns(unit)}
              rows={canReadMovements ? (movements ?? []) : []}
              rowKey={(row) => row.id}
              empty="Nothing has moved for this item yet."
            />
          )
        ) : null}
      </div>
    </Modal>
  );
}

/** The shape of what is loading, not a spinner — a panel of rows arrives as rows. */
function Rows() {
  return (
    <div className="flex flex-col gap-2">
      {[0, 1, 2, 3].map((row) => (
        <Skeleton key={row} className="h-8 w-full" />
      ))}
    </div>
  );
}

/**
 * What the item IS, and what is held of it — both read straight off the row.
 *
 * Two sections rather than one long table: the first is the catalog's answer and the second is
 * the stock's, and a reader scanning for a shortage should not have to pass the unit of measure
 * to reach it.
 */
function ItemInfo({
  level,
  unit,
  agingKnown,
}: {
  level: StockLevelListItem;
  unit: string;
  /** False while the aging call is in flight — see `AGING_PENDING`. */
  agingKnown: boolean;
}) {
  return (
    <div className="flex flex-col gap-5">
      {/*
        TWO SECTIONS rather than one long table: the first is the catalog's answer and the second
        is the stock's, and a reader scanning for a shortage should not have to pass the unit of
        measure to reach it. `sections` is `<PreviewTable>`'s own way of saying that — one table,
        one treatment, two headings.
      */}
      <PreviewTable
        variant="table-display"
        sections={[
          {
            title: 'Item',
            rows: [
          { label: 'Name', value: level.name },
          { label: 'SKU', value: level.sku, mono: true },
          { label: 'Category', value: level.inventoryCategoryName },
          { label: 'Unit', value: unit, mono: true },
          { label: 'Kind', value: ITEM_KIND_LABEL[level.kind] },
          { label: 'Manufacturer/Service Provider', value: level.manufacturer },
          { label: 'Brand', value: level.brandName },
          { label: 'Model', value: level.model },
          { label: 'Reorder level', value: level.reorderLevel, mono: true },
          {
            label: 'Maximum stock level',
            value: level.maxStockLevel === null ? null : level.maxStockLevel,
            mono: true,
          },
          {
            label: 'Stock status',
            /*
              The SAME pill the table's own status column draws, in the same tone. Two
              treatments for one state on two surfaces a reader moves between is the drift the
              shared component exists to prevent.
            */
            value: agingKnown ? (
              <StatusText tone={stockStatusTone(level.stockStatus)}>
                {STOCK_STATUS_LABEL[level.stockStatus]}
              </StatusText>
            ) : (
              AGING_PENDING
            ),
          },
          {
            label: 'Last movement',
            value: level.latestMovementAt === null ? null : formatDate(level.latestMovementAt),
            mono: true,
          },
            ],
          },
          {
            /*
              In the TABLE's own band order — Summary, Issued, For inspection, Unusable, Written
              off, Lease — so a reader moving between the two reads the same figures in the same
              sequence. An ISSUED row that is still an em dash is so for the reason the table's
              own column is: nothing can answer it yet, and a zero would answer it wrongly.
            */
            title: 'Stock summary',
            rows: [
          {
            label: 'Investment',
            value: level.investment === null ? null : formatMoney(level.investment),
            mono: true,
          },
          { label: 'Total on hand', value: quantity(level.onHand, unit) },
          { label: 'Available', value: agingKnown ? quantity(level.available, unit) : AGING_PENDING },
          { label: 'Unusable', value: agingKnown ? quantity(level.unusable, unit) : AGING_PENDING },
          { label: 'Written off', value: quantity(level.writtenOff, unit) },

          { label: 'On loan', value: quantity(level.onLoan, unit) },
          { label: 'Assigned', value: quantity(level.assigned, unit) },

          /*
            DELAYED RETURN sits under On loan because it is a SUBSET of it, not a fourth pool —
            every delayed unit is also on loan, and the two must never be read as adding up.
            The panel has room to say so where a column header does not.
          */
          {
            label: 'Of which overdue',
            value: agingKnown ? quantity(level.delayedReturn, unit) : AGING_PENDING,
          },

          // Still no source at all — nothing in this product writes a sale.
          { label: 'Sold', value: PENDING },

          /*
            CUMULATIVE, and the label says so — every other figure in this panel answers where
            stock is now, and a running total that reads like one of them is the confusion the
            column above spent months as an em dash to avoid.
          */
          { label: 'Dispensed (to date)', value: quantity(level.dispensed, unit) },

          /*
            DELIVERED, NOT YET INSPECTED — and it is the one figure here that is NOT stock.

            It sits between the ISSUED rows and the UNUSABLE ones because that is where the
            table puts its column, and this panel reads in the table’s own band order so a
            reader moving between the two meets the same figures in the same sequence.

            NOTHING ELSE ON THIS PANEL COUNTS IT. On hand, Available and Investment are all
            about stock the organization holds, and a delivery awaiting its official
            inspection has not been registered into any of them — which is the whole reason
            the figure needs saying out loud rather than being left to a reader to infer from
            an item that reads Out of stock while a pallet of it sits on the dock.
          */
          { label: 'For inspection', value: quantity(level.forInspection, unit) },

          {
            /*
              THE WINDOW IS ITSELF AN AGING FIGURE, so the LABEL waits with the value. It
              resolves the item's own threshold against the facility-wide fallback, which
              `Project` leaves at zero — a heading reading "≤0d" is a claim about the setting
              rather than a value still loading.
            */
            label: agingKnown ? `Near expiry (≤${level.nearExpiryDays}d)` : 'Near expiry',
            value: agingKnown ? quantity(level.nearExpiry, unit) : AGING_PENDING,
          },
          { label: 'Expired', value: agingKnown ? quantity(level.expired, unit) : AGING_PENDING },
          { label: 'Damaged', value: quantity(level.damaged, unit) },
          { label: 'Under repair', value: quantity(level.underRepair, unit) },
          { label: 'Quarantined', value: quantity(level.quarantined, unit) },

          { label: 'Condemned', value: quantity(level.condemned, unit) },
          { label: 'Stolen', value: quantity(level.stolen, unit) },

          {
            label: 'Lease ending',
            value: agingKnown ? quantity(level.leaseExpiring, unit) : AGING_PENDING,
          },
          {
            label: 'Lease expired',
            value: agingKnown ? quantity(level.leaseExpired, unit) : AGING_PENDING,
          },
          {
            label: 'Earliest lease end',
            value:
              level.earliestLeaseEnd === null ? null : formatDateOnly(level.earliestLeaseEnd),
            mono: true,
          },
            ],
          },
        ]}
      />
    </div>
  );
}

const batchColumns = (unit: string) => [
  {
    header: 'Lot',
    width: 150,
    render: (row: StockBatchListItem) => <span className="font-mono">{row.batchNumber}</span>,
  },
  { header: 'Facility', width: 170, render: (row: StockBatchListItem) => row.storageFacilityName },
  {
    header: 'On hand',
    width: 110,
    align: 'right' as const,
    render: (row: StockBatchListItem) => quantity(row.quantityOnHand, unit),
  },
  {
    header: 'Received',
    width: 110,
    align: 'right' as const,
    render: (row: StockBatchListItem) => quantity(row.quantityReceived, unit),
  },
  {
    header: 'Expires',
    width: 120,
    render: (row: StockBatchListItem) => (
      <span className="font-mono">
        {row.expiryDate === null ? '—' : formatDateOnly(row.expiryDate)}
      </span>
    ),
  },
  {
    header: 'Status',
    width: 130,
    render: (row: StockBatchListItem) => BATCH_STATUS_LABEL[row.status],
  },
];

/*
  A PANEL COLUMN IS KEYED, and only a field the SERVER can order by is `sortable`.

  `StockUnitService.SortableFields` names the serial, the SKU, the item name, the facility, the
  acquisition and term dates and the two audit instants — so status, lease end and notes are
  deliberately plain text. A header that cannot sort is not a dead button here: it silently falls
  back to the default sort, which reads as a broken control.
*/
/**
 * The units grid, in the order a reader scans it.
 *
 * WHAT identifies the unit (serial), WHERE it is (facility), HOW IT IS HELD (tenure, and the
 * lease's own date where there is one), the standing note — and the CONDITION last, against
 * the kebab, which is where this product puts the column reporting a row’s state.
 *
 * <b>LEASE ENDS is drawn only where the ITEM holds a lease</b>, read off the level row’s
 * `earliestLeaseEnd` rather than off the page of rows in hand. A page is not the item: derived
 * from the rows, the column would appear and vanish as the reader paged — and on an item
 * nothing is leased it would be an em dash on every row, which is worse than a missing column.
 *
 * <b>LEASED FROM sits beside it</b>, and it arrived with the column on 28 Sep 2026 — a
 * product call that reversed a deferral recorded twice. See `StockUnit.LeasedFrom`, which
 * carries the argument and the cost; both columns are drawn on the same condition, because a
 * lessor with no lease is a fact about nothing.
 */
const unitColumns = (level: StockLevelListItem) => [
  {
    key: 'serialNumber',
    header: 'Serial',
    width: 180,
    sortable: true,
    render: (row: StockUnitListItem) => <span className="font-mono">{row.serialNumber}</span>,
  },
  {
    key: 'storageFacilityName',
    header: 'Facility',
    width: 180,
    sortable: true,
    render: (row: StockUnitListItem) => row.storageFacilityName,
  },
  /*
    TENURE, which belongs to the UNIT rather than to the type — two vans of one model can be
    held differently, one bought and one rented, and that is the whole reason this column
    exists beside the catalog item's own.

    A NULL renders an em dash rather than "Owned". The entity's contract is that null falls
    back to the CATALOG ITEM's declaration, and a level row does not carry one — so resolving
    it here would mean stating a tenure this panel cannot actually read. The dash says the
    unit declares none, which is what is true.
  */
  {
    key: 'ownership',
    header: 'Ownership',
    width: 120,
    render: (row: StockUnitListItem) =>
      row.ownership === null ? '—' : OWNERSHIP_LABEL[row.ownership],
  },
  ...(level.earliestLeaseEnd === null
    ? []
    : [
        {
          key: 'leaseEndDate',
          header: 'Lease ends',
          width: 130,
          render: (row: StockUnitListItem) => (
            <span className="font-mono">
              {row.leaseEndDate === null ? '—' : formatDateOnly(row.leaseEndDate)}
            </span>
          ),
        },
        {
          key: 'leasedFrom',
          header: 'Leased from',
          width: 180,
          render: (row: StockUnitListItem) => row.leasedFrom ?? '—',
        },
      ]),
  /*
    THE NOTE THE REGISTRATION GRID COLLECTED, and it was missing until 22 Sep 2026.

    A reader types a note per unit on the registration form and lands here to check what they
    just registered — and this tab showed serial, facility, status and lease end, so the value
    was typed, stored and unreadable one click from where it was written. Only the unit’s own
    profile page carried it. A value collected and never read on the surface that collected it
    is the `ExpiryThresholdDays` failure at short range.

    **It is NOT the Movement History tab's Reason, and the two reading differently is correct.**
    A reason says why stock MOVED and is GENERATED for a receipt — `Received {serial}`, exactly
    as a lot receipt writes `Received {batchNumber}` — because stock arriving is its own reason.
    A note is a standing fact about the thing. The adjustment form asks for both, one line
    apart, which is what makes the split legible rather than a naming accident.
  */
  {
    key: 'notes',
    header: 'Notes',
    width: 220,
    render: (row: StockUnitListItem) => row.notes ?? '—',
  },
  /*
    CONDITION, and it is LAST — against the kebab, which is the position every column
    reporting a row's state takes here. It is the product's own word for it:
    <StockUnitsIndexPage> heads this column Condition and files by a `condition` facet, and
    the row menu beside it says Set condition. It read `status` until 23 Sep 2026, which was
    two words for one concept across two surfaces AND a key the convention scan reads as the
    row's own live-or-deleted marker.

    <b>It is NOT hoisted by `status: true`</b>, and must not borrow that flag to reach the
    end: that key marks the LIVE-OR-DELETED state the shell moves, and a column holding a
    record's own vocabulary taking it would make one marker mean two things. Its position
    here is declared.

    <b>The tone is the shared one</b>, so the list and the panel cannot draw one state two ways.
  */
  {
    key: 'condition',
    header: 'Condition',
    width: 140,
    render: (row: StockUnitListItem) => (
      <StatusText tone={unitConditionTone(row.status)}>{UNIT_STATUS_LABEL[row.status]}</StatusText>
    ),
  },
];

const movementColumns = (unit: string) => [
  {
    header: 'Recorded',
    width: 170,
    render: (row: StockMovementListItem) => (
      <span className="font-mono">{formatDate(row.occurredAt)}</span>
    ),
  },
  {
    header: 'Movement',
    width: 120,
    render: (row: StockMovementListItem) => movementTypeLabel(row.movementType, row.quantity),
  },
  {
    /*
      SIGNED, exactly as the ledger's own table renders it. The sign is what makes the column
      readable: an item's balance is the sum of its movements and nothing else, so a magnitude
      beside a direction word would be the same fact split in two.
    */
    header: 'Quantity',
    width: 110,
    align: 'right' as const,
    render: (row: StockMovementListItem) => (
      <span className="font-mono tabular-nums">
        {row.quantity > 0 ? '+' : ''}
        {row.quantity} <span className="text-slate-400">{unit}</span>
      </span>
    ),
  },
  {
    header: 'Lot / Serial',
    width: 150,
    render: (row: StockMovementListItem) => (
      <span className="font-mono">{row.batchNumber ?? row.serialNumber ?? '—'}</span>
    ),
  },
  { header: 'Facility', width: 160, render: (row: StockMovementListItem) => row.storageFacilityName },
  { header: 'Reason', width: 200, render: (row: StockMovementListItem) => row.reason },
];
